import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api, fileUrl, formatSeconds, formatPace } from "@/lib/api";
import { Trophy, Medal } from "@phosphor-icons/react";

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");

  const activeEvent = events.find((ev) => ev.id === eventId);
  const bgPath = activeEvent?.leaderboard_background_path;

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || []));
  }, []);
  useEffect(() => {
    const q = eventId ? `?event_id=${eventId}` : "";
    api.get(`/categories${q}`).then((r) => setCats(r.data));
    setCat("");
  }, [eventId]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (cat && !cat.startsWith("name:")) params.set("category_id", cat);
    if (eventId) params.set("event_id", eventId);
    const qs = params.toString();
    api.get(`/leaderboard${qs ? `?${qs}` : ""}`).then((r) => {
      const list = r.data.entries;
      setEntries(cat.startsWith("name:") ? list.filter((e) => e.category_name === cat.slice(5)) : list);
    });
  }, [cat, eventId]);

  // All Events: category names repeat per event — show each once, filter by name client-side
  const catOptions = eventId
    ? cats.map((c) => ({ value: c.id, label: c.name }))
    : [...new Map(cats.map((c) => [c.name, c])).values()].map((c) => ({ value: `name:${c.name}`, label: c.name }));

  return (
    <div className="min-h-screen relative z-10">
      {bgPath && (
        <div className="fixed inset-0 -z-10" data-testid="leaderboard-event-bg">
          <img src={fileUrl(bgPath)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
      )}
      <Nav />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">LIVE RANKING</div>
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <h1 className="font-display text-5xl mt-3">LEADERBOARD</h1>
          <div className="flex gap-2 flex-wrap">
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="tactical-input max-w-[260px]" data-testid="leaderboard-event-filter">
              <option value="">All Events</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="tactical-input max-w-[240px]" data-testid="leaderboard-filter">
              <option value="">All Categories</option>
              {catOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-white/40">Peringkat dihitung dari <span className="text-[#FF3B30]">PACE (menit/km)</span> agar adil untuk semua jarak tempuh.</p>

        <div className="mt-4 card-flat overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 md:px-6 py-3 text-[10px] tracking-widest text-white/50 font-display border-b border-white/10">
            <div className="col-span-1">RANK</div>
            <div className="col-span-2">BIB</div>
            <div className="col-span-3">RUNNER</div>
            <div className="col-span-2">CATEGORY</div>
            <div className="col-span-2 text-right">PACE</div>
            <div className="col-span-2 text-right">TIME</div>
          </div>
          {entries.length === 0 && (
            <div className="p-10 text-center text-white/50" data-testid="leaderboard-empty">
              Belum ada peserta yang selesai. Jadilah yang pertama!
            </div>
          )}
          {entries.map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 md:gap-2 px-3 md:px-6 py-3 md:py-4 items-center border-b border-white/5 hover:bg-white/[0.02] fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`lb-row-${e.rank}`}>
              <div className="col-span-1 font-display text-lg md:text-2xl text-[#FF3B30] flex items-center gap-0.5 md:gap-1">
                {e.rank === 1 && <Trophy size={18} weight="fill" color="#FBBF24" className="hidden sm:inline" />}
                {e.rank === 2 && <Medal size={18} weight="fill" color="#D1D5DB" className="hidden sm:inline" />}
                {e.rank === 3 && <Medal size={18} weight="fill" color="#F59E0B" className="hidden sm:inline" />}
                {e.rank}
              </div>
              <div className="col-span-2 font-mono-num text-white/80 text-sm md:text-base">#{e.bib_number}</div>
              <div className="col-span-3 flex items-center gap-3">
                <div>
                  <div className="font-display tracking-wide text-sm md:text-base">{e.full_name?.toUpperCase()}</div>
                  <div className="text-xs text-white/50 hidden sm:block">{e.city}</div>
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-white/80 font-display tracking-widest text-xs md:text-sm">{e.category_name}</div>
                <div className="text-[10px] text-white/40 tracking-widest hidden sm:block">{e.event_name}</div>
              </div>
              <div className="col-span-2 text-right font-mono-num text-sm md:text-base text-[#FF3B30]" data-testid={`lb-pace-${e.rank}`}>{formatPace(e.pace_sec_per_km)}</div>
              <div className="col-span-2 text-right font-mono-num text-xs md:text-sm text-white/80">{formatSeconds(e.time_seconds)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
